import * as Actions from '../actions/matterActions';
import Matter from '../../entities/matter';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type MatterAction = { 
    type: string; 
    matter: Matter | null; 
    matterId: string | Matter; 
    partial: boolean; 
    lastFetch: number; 
    matters: Matter[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Matter> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Matter[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Matter> | null, firmId: string, userId: string, updates?: Matter[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'matter', 'matters', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Matter>} data
 * @param {Matter[]} [updates]
 * @returns {ArrayMap<Matter>}
 */
function updateStorage(data: ArrayMap<Matter>, updates?: Matter[]): ArrayMap<Matter> {
	if (logging) console.debug('⮂ matterReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Matter[]} [updates]
 * @returns {ArrayMap<Matter>}
 */
function updateStorageNoReturn(updates?: Matter[]): void {
	if (logging) console.debug('⮂ matterReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Matter[]} [data]
 * @returns {ArrayMap<Matter>}
 */
function createState(data?: Matter[] | ArrayMap<Matter>): ArrayMap<Matter> {
	if (!data) return ArrayMapRegistry.clone<Matter>('matter', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Matter>('matter', data.map(e => e));
    else return ArrayMapRegistry.clone<Matter>('matter', data);
}

function matters(state: ArrayMap<Matter>, action: MatterAction): ArrayMap<Matter> {
	if (!state) state = createState();

	let st: ArrayMap<Matter>;

	switch (action.type) {
		// action.matter - the new value of matter with id action.id
		// action.matterId - id of the target, used if new value is null
		case Actions.MATTER_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ matterReducer: FETCH_ONE_SUCCESS', action);

			if (action.matter === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.matterId)) {
				st = state.clone();
				st.set(action.matter);
				// state.clear();

				updateStorageNoReturn([action.matter]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.matters
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.matters
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.matters
			 !partial, !stateEmpty, fetchEmpty	-> D return matters in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge matters in state newer than those in action.matters
		 */
		case Actions.MATTER_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.matters.length === 0;
			const fetched = action.matters.map(matter => new Matter({...matter, persisted: true}));

			if (logging) console.debug(`⮂ matterReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} matters with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ matterReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ matterReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Matter> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Matter[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(matter => {
					const current = st.get(matter.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && matter.status === 'D') {
							// Remove it from state
							toUpdate.push(matter);
							st.delete(matter);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (matter.addedOn > current.addedOn) {
								toUpdate.push(matter);
								st.set(matter);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (matter.status !== 'D') {
							toUpdate.push(matter);
							st.set(matter);
						}
						else if (logging) console.debug('⮂ matterReducer: partialMerge: skipping deleted matter not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ matterReducer: empty: matters', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ matterReducer: fullMerge: matters', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localMatter => {
                    // If this matter has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localMatter.persisted) {
                        const fetchedMatter = st.get(localMatter.id);
                        if (!fetchedMatter || localMatter.addedOn > fetchedMatter.addedOn) st.set(localMatter);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.MATTER_FETCH_ONE_FAILURE:
			console.error('MATTER_FETCH_ONE_FAILURE', action.matterId, action.err);

			// No update to storage required
			return state;

		case Actions.MATTER_FETCH_FAILURE:
			console.error('MATTER_FETCH_FAILURE', action.matterId, action.err);

			// No update to storage required
			return state;

		case Actions.MATTER_RESTORE: {
		    let matter = new Matter({...action.matter});
            st = state.clone();

            const existing = st.get(matter.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (matter.addedOn >= existing.addedOn) {
                    st.set(matter);
                    return updateStorage(st, [matter]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (matter.status === 'D') return state;

                st.set(matter);
                return updateStorage(st, [matter]);
            }
        }
            
		case Actions.MATTER_SAVE: {
		    // console.debug('⮂ MATTER_SAVE reducer', action.matter?.id);
		    
			// clone the matter
			let matter = new Matter({...action.matter});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new matter in the state
			// If this is a create, add to the queue
			const _matter = st.get(matter.id);

			if (!_matter) st.set(matter);
			else {
				if (matter.addedOn >= _matter.addedOn) st.set(matter);
			}

			return updateStorage(st, [matter]);
		}

		case Actions.MATTER_SAVE_MANY: {
		    if (logging) console.debug('⮂ MATTER_SAVE_MANY reducer', action.matters.map(matter => matter.id).join(','));
		    
			// clone the matters
			let toProcess = action.matters.map(matter => new Matter(matter));

			st = state.clone();
			// state.clear();

			const updates: Matter[] = [];

			toProcess.forEach(matter => {
				// Add or update the new matter in the state
				// If this is a create, add to the queue
				const existing = st.get(matter.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (matter.status !== 'D') {
						st.push(matter);
						updates.push(matter);
					}
					else if (logging) console.debug('⮂ MATTER_SAVE_MANY: skipping deleted matter not in state');
				}
				else {
					if (matter.addedOn >= existing.addedOn) {
						st.set(matter);
						updates.push(matter);
					}
					else if (logging) console.debug('⮂ Skipping, existing matter had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.MATTER_REMOVE: {
			// clone the matter
			const matter = new Matter({...action.matter});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(matter);

			return updateStorage(st, [matter]);
		}

		case Actions.MATTER_RESTORE_FAILURE:
		case Actions.MATTER_SAVE_FAILURE: {
		    console.warn('MATTER_SAVE_FAILURE reducer', action.matter?.id);
		    if (!action.matter?.id) {
				console.warn('MATTER_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const matter = ensure(Matter, action.matter);
			if (!matter) {
				console.error('Could not find matter after call to save');
				return state;
			}

			st = state.clone();

			const _matterRef = state.get(matter.id);

			if (_matterRef && (_matterRef.addedOn === matter.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _matter = new Matter({..._matterRef});
				_matter.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_matter.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('matter', _matter).then(r => {
						if (r.isErr()) console.error(`⮂ matterReducer: failed to save matter with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ matterReducer: saved matter with fatalError info');
					}).catch(err => console.error(`⮂ matterReducer: failed to save matter with fatalError info: ${err.message}`));

				st.set(_matter);
			}

			return updateStorage(st);
		}

		case Actions.MATTER_SAVE_MANY_FAILURE: {
		    console.warn('MATTER_SAVE_MANY_FAILURE reducer', action.matters.map(matter => matter.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.MATTER_RESTORE_SUCCESS:
		case Actions.MATTER_SAVE_SUCCESS: {
			if (!action.matter?.id) {
				console.warn('MATTER_SAVE_SUCCESS reducer');
                return state;
			}

			const matterRef = state.get(action.matter?.id);
			if (!matterRef) {
				console.warn('Could not find matter after call to save');
                return state;
			}

			st = state.clone();

			if (action.matter.addedOn >= matterRef.addedOn) {
				st.set(action.matter);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const matter = new Matter({...matterRef, persisted: true});
				st.set(matter);
			}

			return updateStorage(st, [action.matter]);
		}

		case Actions.MATTER_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ MATTER_SAVE_MANY_SUCCESS reducer', action.matters.map(matter => matter.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const matters: Matter[] = [];

			action.matters.forEach(e => {
				const matterRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!matterRef) console.warn('Could not find matter after call to save');
				else if (e.addedOn >= matterRef.addedOn) {
				    st.set(e);
				    matters.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const matter = new Matter({...matterRef, persisted: true});
					st.set(matter);
					matters.push(matter);
				}
			});

			return updateStorage(st, matters);
		}

		case Actions.MATTER_REMOVE_FAILURE: {
		    console.warn('MATTER_REMOVE_FAILURE action', action.matter?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.MATTER_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.MATTER_RESTORE_MANY: {
			if (logging) console.debug('⮂ MATTER_RESTORE_MANY reducer', action.matters.map(matter => matter.id).join(','));

			const toProcess = action.matters.map(matter => new Matter({...matter}));
			st = state.clone();

			const updates: Matter[] = [];

			toProcess.forEach(matter => {
				const existing = st.get(matter.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (matter.addedOn >= existing.addedOn) {
						st.set(matter);
						updates.push(matter);
					}
					else if (logging) console.debug('⮂ MATTER_RESTORE_MANY: skipping older version of matter');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (matter.status !== 'D') {
						st.set(matter);
						updates.push(matter);
					}
					else if (logging) console.debug('⮂ MATTER_RESTORE_MANY: skipping deleted matter not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.MATTER_REMOVE_MANY: {
			if (logging) console.debug('⮂ MATTER_REMOVE_MANY reducer', action.matters.map(matter => matter.id).join(','));

			const toProcess = action.matters.map(matter => new Matter({...matter}));
			st = state.clone();

			toProcess.forEach(matter => {
				st.delete(matter);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default matters;